import { ReactElement } from "react";
import { Box, Divider } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import RefreshIcon from "@mui/icons-material/Refresh";
import { nationalities } from "i18n-nationality/langs/de.json";
import {
  Error,
  FilterFormProps,
  type FilterPayload,
  List,
  useGetList,
  useGetRecordId,
  useRecordContext,
} from "react-admin";
import { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import { StudentsListSkeleton } from "../../lists/StudentsListSkeleton";
import { Row } from "../../misc/Row";
import { ExcelExportButton } from "../../buttons/ExcelExportButton";
import { DateTime } from "luxon";
import { currentUser, grants } from "../../backoffice.access_control";
import { DrivingLicenseClassEnum } from "../../model/DrivingLicenseClass";
import { StudentsDataGrid } from "../../lists/StudentsDataGrid";
import { MultiSelectInput } from "../../inputs/MultiSelectInput";
import { Branch } from "../../providers/branchesProvider";
import { MoreFiltersButton } from "../../buttons/MoreFiltersButton";
import { CheckboxGroupInput2 } from "../../inputs/CheckboxGroupInput2";
import { SearchFilterInput } from "../../inputs/SearchFilterInput";
import { FrontendPaymentStrategyEnum } from "../../dialogs/ChangeStudentPaymentStrategyDialog";
import { formatCents } from "../../misc/Money";
import { DateWithoutTime } from "../../model/DateWithoutTime";
import { ResponsiveListSurface } from "../../misc/ResponsiveListSurface";

const nationalityChoices = Object.entries(nationalities)
  .sort(([, nationalityNameA], [, nationalityNameB]) => nationalityNameA.localeCompare(nationalityNameB, "de"))
  .map(([id, name]) => ({ id, name }));

export function DrivingSchoolStudentsList({
  storeKey,
  filter,
  empty,
}: {
  storeKey: string;
  filter?: FilterPayload;
  empty?: ReactElement;
}) {
  const drivingSchoolId = useGetRecordId();
  const drivingSchool = useRecordContext<DrivingSchool>();
  const {
    data: simulators,
    error: simulatorsError,
    refetch: refetchSimulators,
    isFetching: isFetchingSimulators,
  } = useGetList(
    "simulators",
    {
      filter: { drivingSchoolId },
      pagination: { page: 1, perPage: 2 },
      sort: { field: "id", order: "DESC" },
    },
    { enabled: !!drivingSchoolId },
  );
  const {
    data: instructors,
    error: instructorsError,
    refetch: refetchInstructors,
    isFetching: isFetchingInstructors,
  } = useGetList(
    "instructors",
    {
      filter: { drivingSchoolId: drivingSchoolId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );
  const {
    data: activeBranches,
    error: branchesError,
    refetch: refetchBranches,
    isFetching: isFetchingBranches,
  } = useGetList<Branch>(
    "branches",
    {
      filter: { drivingSchoolId: drivingSchoolId },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "name", order: "ASC" },
    },
    { enabled: !!drivingSchoolId },
  );
  const showSimulatorDrivingLessons = !!simulators?.length;
  const dependencyError = simulatorsError ?? instructorsError ?? branchesError;
  const retryDependencies = () => {
    void runSafely(
      () =>
        Promise.all([
          refetchSimulators({ throwOnError: true }),
          refetchInstructors({ throwOnError: true }),
          refetchBranches({ throwOnError: true }),
        ]),
      { errorMessage: "Die Daten konnten nicht erneut geladen werden." },
    );
  };
  if (dependencyError && !(simulators && instructors && activeBranches)) {
    return (
      <ResponsiveListSurface variant="tabContent" data-testid="DrivingSchoolStudents">
        <Error error={dependencyError} resetErrorBoundary={retryDependencies} />
        <Row sx={{ justifyContent: "center", mt: 2 }}>
          <LoadingButton
            variant="outlined"
            startIcon={<RefreshIcon />}
            loading={isFetchingSimulators || isFetchingInstructors || isFetchingBranches}
            onClick={retryDependencies}
          >
            Erneut laden
          </LoadingButton>
        </Row>
      </ResponsiveListSurface>
    );
  }
  if (!(drivingSchool && simulators && instructors && activeBranches)) {
    return (
      <ResponsiveListSurface variant="tabContent" data-testid="DrivingSchoolStudents">
        <StudentsListSkeleton
          variant="full"
          showBranch={drivingSchool ? drivingSchool.branches.length > 1 : undefined}
          showSimulatorDrivingLessons={simulators ? showSimulatorDrivingLessons : undefined}
          showStatusFilter={storeKey !== "studentsWithoutInstructors"}
        />
      </ResponsiveListSurface>
    );
  }
  const primaryFilters = [
    <SearchFilterInput key="q" source="q" alwaysOn resettable />,
    ...(activeBranches.length > 1
      ? [
          <MultiSelectInput
            key="branchId"
            label="Filiale"
            source="branchId"
            variant="text"
            withMode
            choices={[
              { id: "__NO_BRANCH__", name: "Filiale nicht zugewiesen" },
              ...activeBranches.map(({ id, name }) => ({ id, name })),
            ]}
            alwaysOn
          />,
        ]
      : []),
    ...(storeKey !== "studentsWithoutInstructors"
      ? [
          <MultiSelectInput
            key="verboseStatus"
            label="Status"
            source="verboseStatus"
            variant="text"
            withMode
            choices={[
              { id: "Aktiv", name: "Aktiv" },
              { id: "Aktiv (Grundbetrag offen)", name: "Aktiv (Grundbetrag offen)" },
              { id: "Abgebrochen", name: "Abgebrochen" },
              { id: "Inaktiv", name: "Inaktiv" },
              { id: "Pausiert", name: "Pausiert" },
              { id: "Offene Zahlungen", name: "Offene Zahlungen" },
              { id: "Fertig", name: "Fertig" },
            ]}
            alwaysOn
          />,
        ]
      : []),
    <MultiSelectInput
      key="drivingLicenseClasses"
      label="Führerscheinklasse"
      source="drivingLicenseClasses"
      variant="text"
      withMode
      choices={getDrivingLicenseClassFilterOptions()}
      alwaysOn
    />,
    <MultiSelectInput
      key="assignedLabels"
      label="Label"
      source="assignedLabels"
      variant="text"
      withMode
      choices={Object.values(drivingSchool?.labels ?? {})
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ id, name }) => ({ id, name }))}
      alwaysOn
    />,
  ];
  const secondaryFilters = [
    <CheckboxGroupInput2
      key="drivingLicenseApplicationStatus"
      label="Prüfauftrag"
      source="drivingLicenseApplicationStatus"
      choices={[
        { id: "pending", name: "Ausstehend" },
        { id: "approved", name: "Genehmigt" },
        { id: "notApproved", name: "Abgelehnt" },
      ]}
      row={false}
      withMode
      alwaysOn
    />,
    <CheckboxGroupInput2
      key="guaranteedDrivingLessonsPerWeek"
      label="Fahrstunden/Woche"
      source="guaranteedDrivingLessonsPerWeek"
      choices={[
        { id: 2, name: "2 UE/W" },
        { id: 4, name: "4 UE/W" },
        { id: 8, name: "8 UE/W" },
      ]}
      row={false}
      withMode
      alwaysOn
    />,
    <CheckboxGroupInput2
      key="paymentStrategy"
      label="Zahlungsart"
      source="paymentStrategy"
      choices={paymentStrategyChoices}
      row={false}
      withMode
      alwaysOn
    />,
    <CheckboxGroupInput2
      key="progressMilestones"
      label="Fortschritt"
      source="progressMilestones"
      choices={[
        { id: "fullyRegistered", name: "Anmeldung vorhanden" },
        { id: "theoryExamPassed", name: "Theorieprüfung bestanden" },
        { id: "minNormalDrivingLessonsDone", name: "Grundfahrten (mind. 10) vorhanden" },
        { id: "schaltkompetenzDone", name: "Schaltnachweis vorhanden" },
        { id: "specialDrivingLessonsDone", name: "Sonderfahrten vorhanden" },
      ]}
      row={false}
      withMode
      alwaysOn
    />,
    <CheckboxGroupInput2
      key="studentType"
      label="Art"
      source="studentType"
      choices={[
        { id: "newDriver", name: "Ersterwerber" },
        { id: "drivingSchoolChanger", name: "Wechsler" },
        { id: "licenseTransferrer", name: "Umschreiber" },
        { id: "otherStudent", name: "Sonstige" },
      ]}
      row={false}
      withMode
      alwaysOn
    />,
    <CheckboxGroupInput2
      key="onboardingIssues"
      label="Onboarding"
      source="onboardingIssues"
      choices={[
        { id: "noLogin", name: "Ohne Login" },
        { id: "noPaymentMethod", name: "Ohne Zahlungsmethode" },
        { id: "noPrepaidCredits", name: "Ohne Guthaben" },
      ]}
      row={false}
      withMode
      alwaysOn
    />,
    <CheckboxGroupInput2
      key="ausbildungsnachweisState"
      label="Ausbildungsnachweis"
      source="ausbildungsnachweisState"
      choices={[
        { id: "signed", name: "Unterschrieben" },
        { id: "notSigned", name: "Nicht unterschrieben" },
      ]}
      row={false}
      withMode
      alwaysOn
    />,
    <MultiSelectInput
      key="nationality"
      label="Staatsangehörigkeit"
      emptyText="Alle"
      source="nationality"
      withSearch
      choices={nationalityChoices}
      alwaysOn
    />,
    <MultiSelectInput
      key="instructorIds"
      label="Fahrlehrer"
      emptyText="Alle"
      source="instructorIds"
      withMode
      choices={instructors.map(({ id, name }) => ({ id, name }))}
      alwaysOn
    />,
  ];

  return (
    <ResponsiveListSurface variant="tabContent" data-testid="DrivingSchoolStudents">
      <List
        resource="students"
        actions={
          <_StudentListActions
            secondaryFilters={secondaryFilters}
            drivingSchool={drivingSchool}
            showSimulatorDrivingLessons={showSimulatorDrivingLessons}
          />
        }
        storeKey={`${storeKey}@${drivingSchool.id}`}
        title=" " // <-- prevent that the default list title is rendered
        filter={{
          ...filter,
          drivingSchoolId: drivingSchool.id,
          ...(!grants.has("Student:list") ? { instructorIds: currentUser?.uid } : {}),
        }}
        disableSyncWithLocation
        sort={/* initial sort order: */ { field: "name", order: "ASC" }}
        exporter={false}
        filters={primaryFilters}
        empty={empty}
      >
        <StudentsDataGrid
          drivingSchool={drivingSchool}
          showSimulatorDrivingLessons={showSimulatorDrivingLessons}
          empty={empty}
          sx={{
            boxShadow: "none",
          }}
        />
      </List>
    </ResponsiveListSurface>
  );
}

const paymentStrategyChoices: { id: FrontendPaymentStrategyEnum; name: string }[] = [
  { id: "upfrontPayment", name: "Vorauszahlung" },
  { id: "payAsYouDrive", name: "Nach jedem Termin" },
  { id: "purchaseOnAccount", name: "Rechnung" },
];

function _StudentListActions(props: {
  secondaryFilters?: FilterFormProps["filters"];
  drivingSchool: DrivingSchool;
  showSimulatorDrivingLessons: boolean;
}) {
  const { secondaryFilters = [], drivingSchool, showSimulatorDrivingLessons } = props;
  return (
    <Row
      sx={{
        alignItems: "center",
        mt: { xs: 1, sm: "19px" },
        mb: 1,
        "@container (max-width: 599px)": { mt: 1 },
      }}
    >
      <Divider orientation="vertical" flexItem />
      <MoreFiltersButton filters={secondaryFilters} sx={{ ml: "16px" }} />
      <Box sx={{ flex: { xs: "0 0 12px", sm: "1 1 auto" }, minWidth: "12px" }} />
      <ExcelExportButton
        filename="Fahrschüler"
        sheetName="Fahrschüler"
        onlyMappedColumns={true}
        columnMappings={[
          { field: "startDate", label: "Anmeldung" },
          { field: "branchId", label: "Filiale" },
          { field: "firstName", label: "Vorname" },
          { field: "lastName", label: "Nachname" },
          { field: "dateOfBirth", label: "Geburtsdatum" },
          { field: "email", label: "E-Mail" },
          { field: "phoneNumber", label: "Telefonnummer" },
          { field: "postalAddress.street", label: "Straße" },
          { field: "postalAddress.postalCode", label: "PLZ" },
          { field: "postalAddress.city", label: "Ort" },
          { field: "isSchoolChanger", label: "Wechsel" },
          { field: "bookedTrainingsSummary", label: "Ausbildung" },
          { field: "kpis.finishedTheoryLessonsCount", label: "Theorieunterricht" },
          ...(showSimulatorDrivingLessons
            ? [{ field: "kpis.finishedSimulatorDrivingLessonsCount", label: "Simulatorstunden" }]
            : []),
          { field: "kpis.finishedNormalDrivingLessonsCount", label: "Fahrstunden" },
          { field: "kpis.finishedSpecialDrivingLessonsCount", label: "Sonderfahrten" },
          { field: "kpis.finishedManualGearLessonsCount", label: "Schaltstunden" },
          { field: "verboseStatus", label: "Status" },
          { field: "balance", label: "Saldo" },
        ]}
        valueFormatters={{
          startDate: (value) => (value ? (value as DateTime).toFormat("dd.MM.yyyy") : ""),
          dateOfBirth: (value) => (value instanceof DateWithoutTime ? value.toDateTime().toFormat("dd.MM.yyyy") : ""),
          branchId: (_value, record) => {
            const branchId = record.branchId ?? drivingSchool.defaultBranch?.uid;
            return drivingSchool.branchesById[branchId]?.name ?? "";
          },
          "kpis.finishedTheoryLessonsCount": formatKpi,
          "kpis.finishedSimulatorDrivingLessonsCount": formatKpi,
          "kpis.finishedNormalDrivingLessonsCount": formatKpi,
          "kpis.finishedSpecialDrivingLessonsCount": formatKpi,
          "kpis.finishedManualGearLessonsCount": formatKpi,
          balance: (value) => (typeof value === "number" ? formatCents(value) : ""),
        }}
      />
    </Row>
  );
}

function formatKpi(value: unknown): number | "" {
  return typeof value === "number" && Number.isFinite(value) ? Math.round(value * 100) / 100 : "";
}

function getDrivingLicenseClassFilterOptions() {
  const options: Array<{ id: string; name: string }> = DrivingLicenseClassEnum.options.map((val) => ({
    id: val,
    name: val,
  }));
  // Inject BF17 option before B78.
  // BF17 is not a drivingLicenseClass, but it's a boolean in the student model, and it's required to be rendered as a drivingLicenseClass option in the UI.
  const b78OptionIndex = options.findIndex((option) => option.id === "B78");
  options.splice(b78OptionIndex, 0, { id: "BF17", name: "BF17" });
  return options;
}
